import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  duplicatePackage,
  duplicatePackageError,
  duplicatePackageResponse,
  getPackagesList,
  getPackagesListResponse,
  loadMorePackagesList,
  loadMorePackagesListResponse,
  removePackage,
  removePackageError,
  removePackageItem,
  removePackageResponse,
  rollbackPackage,
  rollbackPackageError,
  rollbackPackageResponse,
  savePackage,
  savePackageError,
  savePackageResponse,
  searchPackagesList,
  searchPackagesListResponse,
  updatePackage,
  updatePackageError,
  updatePackageResponse,
  getPackagesListError,
} from './packages.actions';
import { PackagesResource } from '../resources/packages.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { NotifyService } from '../../common/services/notify.service';
import { Lazy } from '../../lazy';

@Injectable()
export class PackagesEffects {
  @Effect()
  getPackages$ = this.actions$.pipe(
    ofType(getPackagesList),
    switchMap(({ params }) =>
      this.packagesResource.query(params).pipe(
        map((packages) => getPackagesListResponse({ packages })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getPackagesListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  searchPackages$ = this.actions$.pipe(
    ofType(searchPackagesList),
    switchMap(({ params }) =>
      this.packagesResource.search(params).pipe(map((packages) => searchPackagesListResponse({ packages }))),
    ),
  );

  @Effect()
  loadMorePackages$ = this.actions$.pipe(
    ofType(loadMorePackagesList),
    switchMap(({ params }) =>
      (params.q ? this.packagesResource.search(params) : this.packagesResource.query(params)).pipe(
        map((packages) => loadMorePackagesListResponse({ packages })),
      ),
    ),
  );

  @Effect()
  savePackage$ = this.actions$.pipe(
    ofType(savePackage),
    switchMap(({ packageItem, redirect }) =>
      this.packagesResource.save(packageItem, { include: 'owner,workspace' } as any).pipe(
        map((packageResponse) => {
          if (redirect) {
            this.$rootScope.stateGo('app.account.packages.item.edit', { id: packageResponse.id });
          }
          return savePackageResponse({ data: packageResponse });
        }),
        catchError((response) => of(savePackageError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  duplicatePackage$ = this.actions$.pipe(
    ofType(duplicatePackage),
    switchMap(({ packageId, packageName }) =>
      this.packagesResource
        .save({ source_package_id: packageId, source_package_name: packageName }, { include: 'owner,workspace' } as any)
        .pipe(
          map((packageResponse) => duplicatePackageResponse({ data: packageResponse })),
          catchError((response) => of(duplicatePackageError({ error: response.error }))),
        ),
    ),
  );

  @Effect()
  rollbackPackage$ = this.actions$.pipe(
    ofType(rollbackPackage),
    switchMap(({ packageId, versionId }) =>
      this.packagesResource.rollback(packageId, versionId, { include: 'owner,workspace' } as any).pipe(
        map((packageResponse) => {
          window.location.reload();
          return rollbackPackageResponse({ data: packageResponse });
        }),
        catchError((response) => of(rollbackPackageError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updatePackage$ = this.actions$.pipe(
    ofType(updatePackage),
    switchMap(({ packageItem, params, packageId }) =>
      this.packagesResource.update(packageId, packageItem, { ...params, include: 'owner,workspace' } as any).pipe(
        map((packageResponse) => {
          this.notify.success(this.translate.instant('designer.controls.actions.save_package.success'));
          return updatePackageResponse({ data: packageResponse });
        }),
        catchError((response) => of(updatePackageError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removePackage$ = this.actions$.pipe(
    ofType(removePackage),
    switchMap(({ packageId }) =>
      this.packagesResource.remove(packageId).pipe(
        map((packageResponse) => removePackageItem({ data: packageResponse })),
        catchError((response) => of(removePackageError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removePackageResponse$ = this.actions$.pipe(
    ofType(removePackageItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removePackageResponse({ data })),
  );

  private _$rootScope = new Lazy<any>(() => this.injector.get('$rootScope'));

  constructor(
    private actions$: Actions,
    private packagesResource: PackagesResource,
    private readonly injector: Injector,
    private translate: TranslateService,
    private notify: NotifyService,
  ) {}

  private get $rootScope() {
    // eslint-disable-next-line no-underscore-dangle
    return this._$rootScope.value;
  }
}
